/**
 * /ai/agents — the 4-agent registry + reasoning trace viewer
 * (M3X.1 v2 F.3 + F.5 UI).
 *
 * Renders the four named agents (Tashghil / Rawatib / Mutaba'aa /
 * Khidmat) with goal / domain / authorization scope per mode +
 * live heartbeat health. Each card links to the agent's reasoning
 * trace stream from GET /api/agents/reasoning.
 *
 * Per commandment 65 — every UI surface attributing AI must name
 * the responsible agent. This page is the directory.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useState } from "react";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { AgentBadge } from "../components/ui/AgentBadge";
import { api } from "../lib/api";
import type { AgentName } from "../components/ui/AgentBadge";

const HEALTH_TONES: Record<string, string> = {
  healthy: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  degraded: "bg-amber-50 text-amber-700 border border-amber-200",
  down: "bg-rose-50 text-rose-700 border border-rose-200",
  unknown: "bg-slate-50 text-slate-700 border border-slate-200",
};

export function AgentsPage() {
  const { t } = useTranslation();
  const [selectedAgent, setSelectedAgent] = useState<AgentName | null>(
    null,
  );

  const agentsQuery = useQuery({
    queryKey: ["agents"],
    queryFn: api.listAgents,
    refetchInterval: 30_000,
  });

  const tracesQuery = useQuery({
    queryKey: ["agent-reasoning", selectedAgent ?? "_all_"],
    queryFn: () =>
      api.listAgentReasoningTraces({
        agent: selectedAgent ?? undefined,
        limit: 25,
      }),
    enabled: selectedAgent !== null,
  });

  if (agentsQuery.isLoading) return <Loading />;
  if (agentsQuery.isError)
    return (
      <ErrorPanel message={(agentsQuery.error as Error).message} />
    );

  const agents = agentsQuery.data?.agents ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("agent.page.title")}
        subtitle={t("agent.page.subtitle")}
      />

      {agents.length === 0 ? (
        <EmptyState headline="No agents registered" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {agents.map((agent) => (
            <button
              key={agent.agent_id}
              type="button"
              onClick={() =>
                setSelectedAgent(agent.agent_id as AgentName)
              }
              className="text-start bg-surface-default border border-border-default rounded-lg p-4 hover:border-brand-blue/40 transition-colors"
              data-testid={`agent-card-${agent.agent_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <AgentBadge
                  agent={agent.agent_id as AgentName}
                  variant="full"
                />
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                    HEALTH_TONES[agent.health] ?? HEALTH_TONES.unknown
                  }`}
                >
                  {t(`agent.health.${agent.health}`)}
                </span>
              </div>
              <p className="text-sm text-text-secondary mb-3">
                {agent.goal_statement}
              </p>
              <div className="text-xs text-text-tertiary mb-2">
                <span className="font-medium">
                  {t("agent.page.handoff_vocabulary")}:{" "}
                </span>
                {agent.handoff_vocabulary.join(", ")}
              </div>
              <div className="text-xs text-text-tertiary mb-2">
                <span className="font-medium">Domain: </span>
                {agent.domain.slice(0, 4).join(" · ")}
                {agent.domain.length > 4 ? " · …" : null}
              </div>
              <details className="text-xs text-text-tertiary mt-2">
                <summary className="cursor-pointer">
                  {t("agent.page.authorization_scope")}
                </summary>
                <div className="mt-2 space-y-1">
                  {Object.entries(agent.authorization_scope).map(
                    ([mode, actions]) => (
                      <div key={mode}>
                        <span className="font-medium">
                          {t(`agent.page.by_mode.${mode}`)}:
                        </span>{" "}
                        {actions.join(", ") || "—"}
                      </div>
                    ),
                  )}
                </div>
              </details>
              {agent.last_heartbeat_at && (
                <div className="text-xs text-text-tertiary mt-2">
                  {t("agent.page.last_heartbeat")}:{" "}
                  {new Date(agent.last_heartbeat_at).toLocaleString()}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {selectedAgent && (
        <div className="bg-surface-default border border-border-default rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold">
              {t("agent.page.reasoning_traces")} —{" "}
              <AgentBadge agent={selectedAgent} />
            </h3>
            <button
              type="button"
              onClick={() => setSelectedAgent(null)}
              className="text-xs text-text-tertiary underline"
            >
              ✕ close
            </button>
          </div>
          {tracesQuery.isLoading ? (
            <Loading />
          ) : (tracesQuery.data?.traces ?? []).length === 0 ? (
            <EmptyState headline={t("agent.page.no_traces")} />
          ) : (
            <div className="space-y-2">
              {(tracesQuery.data?.traces ?? []).map((trace) => (
                <div
                  key={trace.id}
                  className="border border-border-subtle rounded p-3 text-sm"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium">
                      {trace.decision_id}
                    </span>
                    <span className="text-xs text-text-tertiary">
                      {new Date(trace.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-text-secondary">
                    {trace.goal_pursued}
                  </div>
                  {trace.confidence !== null && (
                    <div className="text-xs text-text-tertiary mt-1">
                      Confidence:{" "}
                      {Math.round((trace.confidence ?? 0) * 100)}%
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
